import { useCallback } from 'react'
import { useNavigate, useParams, useLocation, type NavigateOptions } from 'react-router-dom'
import {
  buildLocalePath,
  parseLocalePath,
  getDefaultLanguageForLocale,
  isValidLocale,
  isValidLanguage,
  DEFAULT_LOCALE,
  type Locale,
  type Language,
} from '@/lib/router'

/**
 * Locale-aware navigation hook. Always navigates to paths under the current locale prefix.
 * Use instead of useNavigate() anywhere you build an internal URL.
 */
export function useLocaleNavigate() {
  const reactNavigate = useNavigate()
  const params = useParams<{ locale: string; lang: string }>()
  const location = useLocation()

  const locale: Locale = isValidLocale(params.locale) ? params.locale : DEFAULT_LOCALE
  const lang: Language = isValidLanguage(params.lang) ? params.lang : getDefaultLanguageForLocale(locale)

  /** Navigate to a path relative to the current locale prefix. */
  const navigate = useCallback(
    (path: string, options?: NavigateOptions) => {
      reactNavigate(buildLocalePath(locale, lang, path), options)
    },
    [reactNavigate, locale, lang]
  )

  /** Build a full locale-prefixed path without navigating. */
  const buildPath = useCallback(
    (path: string) => buildLocalePath(locale, lang, path),
    [locale, lang]
  )

  /** Switch to a different locale, preserving the current page path. */
  const switchLocale = useCallback(
    (targetLocale: Locale, targetLang?: Language) => {
      const finalLang = targetLang ?? getDefaultLanguageForLocale(targetLocale)
      const rest = parseLocalePath(location.pathname)?.rest ?? '/'
      reactNavigate(buildLocalePath(targetLocale, finalLang, rest), { replace: true })
    },
    [reactNavigate, location.pathname]
  )

  return { navigate, buildPath, switchLocale, locale, lang }
}
